import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Field } from "@/components/Atoms";
import { UserSearchInput } from "@/components/admin/UserSearchInput";
import {
  Gift, RefreshCw, Search, Sparkles, Trash2, Users, Wand2, X, Plus,
  AlertTriangle, CheckCircle2, RotateCcw, Copy,
} from "lucide-react";

/* ===========================================================================
   Give Menu
   ---------------------------------------------------------------------------
   Sends items / pals / eggs / tech / XP to players' in-game inventories over
   RCON. Three things drive the layout:

     1. RCON only delivers to a character that is currently loaded, so online
        state is shown up-front rather than discovered from a failed batch.
     2. The same queue usually goes to several people (event payouts), so
        recipients are a multi-select, not a single dropdown.
     3. Pals can carry a full spec (IVs, passives, souls, stars, lucky). Those
        go out via `givepal_j` + an SFTP'd JSON template; plain level-only
        pals still use the simpler `givepal` command.
   ========================================================================= */

const MAX_BULK = 50;               // pal/egg entries queued per click
// Item presets run to 9,999 — the amount actually reached for on event
// payouts. Pals/eggs filter this list down to <= MAX_BULK further below,
// since those queue one entry per unit rather than one command for N.
const QTY_PRESETS = [1, 10, 50, 100, 500, 999, 5000, 9999];
const PAGE_SIZE = 120;             // catalog tiles rendered before "show more"
const QUEUE_STORAGE_KEY = "chillet_give_queue_draft";

const PLATFORM_BADGE = {
  steam: "🟢 Steam", gdk: "🔵 GDK/PSN", ps5: "🔵 PS5", xbox: "🟢 Xbox", unknown: "⚪",
};

/** Blank spec — every field the backend's PalSpec accepts. */
const emptySpec = (level = 1) => ({
  level,
  passives: [],
  talents: [0, 0, 0],
  rank: 0,
  rank_hp: 0,
  rank_attack: 0,
  rank_defence: 0,
  rank_craft_speed: 0,
  gender: "Male",
  nick_name: "",
  is_lucky: false,
  is_boss: false,
  trust: 0,
});

/** Does this spec ask for anything plain `givepal` can't do? Mirrors the
 *  backend's `_spec_is_meaningful` so the badge and the delivery path agree. */
const specIsCustom = (s) => {
  if (!s) return false;
  if (s.passives?.length || s.nick_name) return true;
  if (s.talents?.some((t) => Number(t) > 0)) return true;
  if (["rank", "rank_hp", "rank_attack", "rank_defence", "rank_craft_speed", "trust"]
      .some((k) => Number(s[k]) > 0)) return true;
  if (s.is_lucky || s.is_boss) return true;
  if ((s.gender || "Male") !== "Male") return true;
  return false;
};

export function Give() {
  const toast = useToast();

  // ---- target selection -------------------------------------------------
  const [servers, setServers] = useState([]);
  const [server, setServer] = useState("");
  const [players, setPlayers] = useState([]);
  const [recipients, setRecipients] = useState([]);   // [{uid, name, server}]
  const [manualUid, setManualUid] = useState("");
  const [playerFilter, setPlayerFilter] = useState("");

  // ---- online presence --------------------------------------------------
  const [online, setOnline] = useState({ uids: [], players: [], available: null, error: "" });
  const [onlineBusy, setOnlineBusy] = useState(false);

  // ---- catalog ----------------------------------------------------------
  const [kind, setKind] = useState("item");
  const [catalog, setCatalog] = useState([]);
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [addQty, setAddQty] = useState(1);

  // ---- queue ------------------------------------------------------------
  const [queue, setQueue] = useState(() => {
    try {
      const raw = localStorage.getItem(QUEUE_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [editingIdx, setEditingIdx] = useState(null);

  // ---- send -------------------------------------------------------------
  const [running, setRunning] = useState(false);
  const [lastResults, setLastResults] = useState(null);
  const [palRef, setPalRef] = useState({ caps: null, passives: [], modded: false });

  const crossServer = server === "__all__";

  /* ---------------------------------------------------------------- load */

  useEffect(() => {
    api.get("/live/servers").then(({ data }) => {
      const list = data.servers || [];
      setServers(list);
      if (list.length > 0) setServer(list[0].name || list[0].slug);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!server) return;
    const url = crossServer
      ? "/admin/give/players"
      : `/admin/give/players?server=${encodeURIComponent(server)}`;
    api.get(url)
      .then(({ data }) => setPlayers(data.players || []))
      .catch(() => setPlayers([]));
    // Recipients are server-scoped — dropping them avoids silently sending
    // to someone who isn't on the newly chosen server.
    setRecipients([]);
  }, [server, crossServer]);

  useEffect(() => {
    setCatalogBusy(true);
    api.get(`/admin/give/catalog?kind=${kind}`)
      .then(({ data }) => setCatalog(data.results || []))
      .catch(() => setCatalog([]))
      .finally(() => setCatalogBusy(false));
  }, [kind]);

  // Pal caps + passive list follow the chosen server (modded servers allow
  // IV 255 / souls 1020, vanilla stays 100 / 20).
  useEffect(() => {
    if (!server) return;
    const q = crossServer ? "" : `?server=${encodeURIComponent(server)}`;
    api.get(`/admin/give/pal-reference${q}`)
      .then(({ data }) => setPalRef({
        caps: data.caps, passives: data.passives || [], modded: !!data.modded,
      }))
      .catch(() => {});
  }, [server, crossServer]);

  const refreshOnline = useCallback(async (quiet = true) => {
    if (!server || crossServer) { setOnline({ uids: [], players: [], available: null, error: "" }); return; }
    setOnlineBusy(true);
    try {
      const { data } = await api.get(`/admin/give/online?server=${encodeURIComponent(server)}`);
      setOnline({
        uids: data.uids || [],
        // The endpoint has always returned name + uid per player; we used to
        // throw that away and keep a count, which meant the admin could see
        // "3 online" but not who, and had to hunt them out of the linked list.
        players: data.players || [],
        available: !!data.available,
        error: data.error || "",
      });
      if (!quiet && data.available) toast.success(`${data.count} online`);
    } catch (e) {
      setOnline({ uids: [], players: [], available: false, error: apiError(e) });
    } finally {
      setOnlineBusy(false);
    }
  }, [server, crossServer, toast]);

  useEffect(() => { refreshOnline(true); }, [refreshOnline]);
  useEffect(() => {
    if (!server || crossServer) return undefined;
    const t = setInterval(() => refreshOnline(true), 30000);
    return () => clearInterval(t);
  }, [server, crossServer, refreshOnline]);

  // Persist the queue so navigating to another admin tab mid-build doesn't
  // lose it — rebuilding a 20-entry bundle from scratch is a real cost.
  useEffect(() => {
    try { localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue)); } catch { /* quota */ }
  }, [queue]);

  /* ------------------------------------------------------------- catalog */

  const catalogCategories = useMemo(() => {
    const counts = new Map();
    catalog.forEach((c) => {
      const k = c.category || "Other";
      counts.set(k, (counts.get(k) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [catalog]);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.filter((c) => {
      if (category && (c.category || "Other") !== category) return false;
      if (!q) return true;
      return c.name?.toLowerCase().includes(q) || c.id?.toLowerCase().includes(q);
    });
  }, [catalog, search, category]);

  useEffect(() => { setVisible(PAGE_SIZE); }, [search, category, kind]);

  const shown = matches.slice(0, visible);

  /* --------------------------------------------------------------- queue */

  const clampedQty = (cap) => Math.max(1, Math.min(addQty || 1, cap));
  const addToQueue = (entry) => setQueue((q) => [...q, entry]);

  const addItem = (it) => addToQueue({
    type: "item", item_id: it.id, item_name: it.name, image_url: it.image,
    amount: clampedQty(99999),
  });
  const addPal = (it) => {
    const n = clampedQty(MAX_BULK);
    setQueue((q) => [...q, ...Array.from({ length: n }, () => ({
      type: "pal", item_id: it.id, item_name: it.name, image_url: it.image,
      level: 1, pal_spec: null,
    }))]);
  };
  const addEgg = (it) => {
    const n = clampedQty(MAX_BULK);
    setQueue((q) => [...q, ...Array.from({ length: n }, () => ({
      type: "egg", item_id: it.id, item_name: it.name, image_url: it.image,
    }))]);
  };
  const addTech = (it) => addToQueue({
    type: "tech", item_id: it.id, item_name: it.name, image_url: it.image,
  });
  const addXp = () => addToQueue({ type: "xp", amount: 10000 });

  const patchRow = (i, patch) =>
    setQueue((q) => q.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  const removeRow = (i) => setQueue((q) => q.filter((_, j) => j !== i));
  const duplicateRow = (i, times = 1) => setQueue((q) => {
    const row = q[i];
    if (!row) return q;
    // Deep-copy the spec so editing the copy doesn't mutate the original.
    const copies = Array.from({ length: Math.max(1, times) }, () => ({
      ...row, pal_spec: row.pal_spec ? { ...row.pal_spec, passives: [...(row.pal_spec.passives || [])], talents: [...(row.pal_spec.talents || [])] } : null,
    }));
    return [...q.slice(0, i + 1), ...copies, ...q.slice(i + 1)];
  });

  /* ---------------------------------------------------------- recipients */

  const toggleRecipient = (p) => {
    const uid = p.player_userid;
    if (!uid) return;
    const adding = !recipients.some((x) => x.uid === uid);
    // An old-style link stores a platform id, which RCON give/givepal
    // can't address — say so up front instead of letting the batch fail.
    if (adding && p.needs_relink) {
      toast.error(
        `${p.player_name || uid} has an old-style link — this send will fail until they log in once and get re-linked.`,
      );
    }
    setRecipients((r) => r.some((x) => x.uid === uid)
      ? r.filter((x) => x.uid !== uid)
      : [...r, { uid, name: p.player_name || uid, server: p.server_name || server }]);
  };

  /** Select every player currently in-game. The common case for an event
   *  payout is "everyone who showed up", which was a tap per person before. */
  const addAllOnline = () => {
    if (!online.available || online.players.length === 0) return;
    setRecipients((r) => {
      const have = new Set(r.map((x) => x.uid));
      const additions = online.players
        .filter((p) => p.player_userid && !have.has(p.player_userid))
        .map((p) => ({
          uid: p.player_userid,
          name: p.player_name || p.player_userid,
          server,
        }));
      return [...r, ...additions];
    });
  };

  /** uid (lowercased) -> linked roster row, so the online list can show a
   *  Discord handle. ShowPlayers only reports the in-game character name. */
  const linkedByUid = useMemo(() => {
    const m = {};
    players.forEach((p) => {
      if (p.player_userid) m[p.player_userid.toLowerCase()] = p;
    });
    return m;
  }, [players]);

  const addManual = () => {
    const uid = manualUid.trim();
    if (!uid) return;
    if (crossServer) {
      toast.error("Pasted UIDs need a specific server — switch off cross-server mode.");
      return;
    }
    if (recipients.some((x) => x.uid === uid)) { setManualUid(""); return; }
    setRecipients((r) => [...r, { uid, name: uid, server }]);
    setManualUid("");
  };

  const filteredPlayers = useMemo(() => {
    const q = playerFilter.trim().toLowerCase();
    if (!q) return players;
    return players.filter((p) =>
      p.player_name?.toLowerCase().includes(q)
      || p.discord_username?.toLowerCase().includes(q)
      || p.player_userid?.toLowerCase().includes(q));
  }, [players, playerFilter]);

  const isOnline = (uid) => online.available === true && online.uids.includes((uid || "").toLowerCase());
  const offlineRecipients = online.available === true
    ? recipients.filter((r) => !isOnline(r.uid))
    : [];

  /* -------------------------------------------------------------- execute */

  const execute = async () => {
    if (recipients.length === 0 || queue.length === 0) {
      toast.error("Pick at least one recipient and one entry.");
      return;
    }
    if (offlineRecipients.length > 0) {
      const names = offlineRecipients.map((r) => r.name).join(", ");
      const go = window.confirm(
        `${offlineRecipients.length} recipient(s) look offline: ${names}\n\n`
        + "RCON only delivers to loaded characters, so these will almost certainly fail. Send anyway?"
      );
      if (!go) return;
    }

    // One request per server — the backend takes a single server_name and
    // cross-server mode can span both.
    const groups = new Map();
    recipients.forEach((r) => {
      const srv = crossServer ? r.server : server;
      if (!srv) return;
      if (!groups.has(srv)) groups.set(srv, []);
      groups.get(srv).push(r.uid);
    });

    setRunning(true);
    try {
      const merged = { delivered: 0, total: 0, results: [], recipients: [] };
      for (const [srv, uids] of groups.entries()) {
        const { data } = await api.post("/admin/give/execute", {
          server_name: srv,
          player_userids: uids,
          entries: queue,
        });
        merged.delivered += data.delivered || 0;
        merged.total += data.total || 0;
        merged.results.push(...(data.results || []));
        merged.recipients.push(...(data.recipients || []).map((x) => ({ ...x, server: srv })));
      }
      setLastResults(merged);
      if (merged.delivered === merged.total) {
        toast.success(`Delivered ${merged.delivered}/${merged.total} across ${recipients.length} player(s)`);
        setQueue([]);
      } else {
        toast.error(`${merged.total - merged.delivered} of ${merged.total} failed — see the results panel`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunning(false);
    }
  };

  /** Rebuild the queue from just the entries that failed, so a partial
   *  batch can be re-sent without hand-rebuilding it. */
  const retryFailed = () => {
    const failed = (lastResults?.results || []).filter((r) => !r.ok && r.entry);
    if (failed.length === 0) return;
    const seen = new Set();
    const rebuilt = [];
    failed.forEach((r) => {
      const key = JSON.stringify(r.entry);
      const uidKey = `${r.player_userid}|${key}`;
      if (seen.has(uidKey)) return;
      seen.add(uidKey);
      if (!rebuilt.some((e) => JSON.stringify(e) === key)) rebuilt.push(r.entry);
    });
    setQueue(rebuilt);
    toast.success(`Queue rebuilt with ${rebuilt.length} failed entr${rebuilt.length === 1 ? "y" : "ies"}`);
  };

  const caps = palRef.caps || { level_max: 80, iv_max: 100, soul_max: 20, rank_max: 4, trust_max: 3, passive_max: 4 };

  /* ---------------------------------------------------------------- view */

  return (
    <div className="space-y-4 fade-up" data-testid="give-tab">
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1 flex items-center gap-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
          <Gift size={18} className="text-[var(--teal)]" /> Give Menu
        </h3>
        <p className="text-xs text-[var(--muted)]">
          Send items, pals, eggs, tech unlocks, or XP straight to players&apos; in-game inventories via RCON.
          Recipients must be online. Pals can carry a full spec (IVs, passives, souls, stars) — those deliver
          through <code>givepal_j</code>. Mirrors the bot&apos;s <code>/givemenu</code>.
        </p>
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] gap-4">
        {/* ================= left: targets, queue, results ================= */}
        <div className="space-y-3 min-w-0">
          <RecipientPicker
            servers={servers}
            server={server}
            setServer={setServer}
            crossServer={crossServer}
            players={filteredPlayers}
            playerFilter={playerFilter}
            setPlayerFilter={setPlayerFilter}
            recipients={recipients}
            toggleRecipient={toggleRecipient}
            setRecipients={setRecipients}
            manualUid={manualUid}
            setManualUid={setManualUid}
            addManual={addManual}
            online={online}
            onlineBusy={onlineBusy}
            refreshOnline={refreshOnline}
            isOnline={isOnline}
            addAllOnline={addAllOnline}
            linkedByUid={linkedByUid}
          />

          <QueuePanel
            queue={queue}
            setQueue={setQueue}
            patchRow={patchRow}
            removeRow={removeRow}
            duplicateRow={duplicateRow}
            onEditSpec={setEditingIdx}
            recipients={recipients}
            running={running}
            onExecute={execute}
            offlineCount={offlineRecipients.length}
          />

          {lastResults && (
            <ResultsPanel results={lastResults} onRetry={retryFailed} onDismiss={() => setLastResults(null)} />
          )}
        </div>

        {/* ====================== right: catalog ========================== */}
        <CatalogBrowser
          kind={kind}
          setKind={setKind}
          catalog={catalog}
          catalogBusy={catalogBusy}
          categories={catalogCategories}
          category={category}
          setCategory={setCategory}
          search={search}
          setSearch={setSearch}
          matches={matches}
          shown={shown}
          visible={visible}
          setVisible={setVisible}
          addQty={addQty}
          setAddQty={setAddQty}
          onAdd={{ item: addItem, pal: addPal, egg: addEgg, tech: addTech }}
          onAddXp={addXp}
        />
      </div>

      {editingIdx !== null && queue[editingIdx] && (
        <PalSpecModal
          row={queue[editingIdx]}
          caps={caps}
          passives={palRef.passives}
          modded={palRef.modded}
          onClose={() => setEditingIdx(null)}
          onSave={(spec, level) => {
            patchRow(editingIdx, { pal_spec: spec, level });
            setEditingIdx(null);
          }}
        />
      )}
    </div>
  );
}

/* =========================================================================
   Recipients
   ======================================================================= */

function RecipientPicker({
  servers, server, setServer, crossServer, players, playerFilter, setPlayerFilter,
  recipients, toggleRecipient, setRecipients, manualUid, setManualUid, addManual,
  online, onlineBusy, refreshOnline, isOnline, addAllOnline, linkedByUid,
}) {
  return (
    <div className="glass-card p-4 space-y-3" data-testid="give-recipients">
      <Field label="Server">
        <select className="input" value={server} onChange={(e) => setServer(e.target.value)} data-testid="give-server-select">
          {servers.map((s) => (
            <option key={s.slug} value={s.name || s.slug}>{s.name || s.slug}</option>
          ))}
          <option value="__all__">🌐 All servers (cross-server search)</option>
        </select>
      </Field>

      {/* Who's actually in-game right now. These are the only players a give
          can reach, so they get their own picker rather than being a count
          the admin then has to reconcile against the linked-players list.
          Names come straight from RCON's ShowPlayers, so unlinked players
          appear here too — and can be given to, since a UID is all we need. */}
      <div className="rounded-md bg-black/30 border border-white/5 p-2.5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="label !mb-0 flex items-center gap-1.5">
            {online.available ? (
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
            ) : (
              <AlertTriangle size={11} className="text-amber-300 shrink-0" />
            )}
            Online now{online.available ? ` (${online.players.length})` : ""}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {online.available && online.players.length > 1 && (
              <button
                type="button"
                onClick={addAllOnline}
                className="btn btn-sm btn-secondary !px-2"
                data-testid="give-add-all-online"
              >
                Add all
              </button>
            )}
            <button
              type="button"
              onClick={() => refreshOnline(false)}
              disabled={onlineBusy || crossServer}
              className="btn btn-sm btn-secondary !px-2"
              title="Re-check online players"
              data-testid="give-online-refresh"
            >
              <RefreshCw size={12} className={onlineBusy ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {crossServer ? (
          <p className="text-[11px] text-[var(--muted)]">
            Pick a single server to see who&apos;s in-game.
          </p>
        ) : online.available === null ? (
          <p className="text-[11px] text-[var(--muted)]">Checking who&apos;s online…</p>
        ) : !online.available ? (
          <p className="text-[11px] text-[var(--muted)]" title={online.error}>
            Can&apos;t reach RCON — online status unknown. You can still send, but
            delivery will fail for anyone not in-game.
          </p>
        ) : online.players.length === 0 ? (
          <p className="text-[11px] text-[var(--muted)]">
            Nobody is in-game on this server right now. Gives will not deliver.
          </p>
        ) : (
          <ul className="space-y-1" data-testid="give-online-list">
            {online.players.map((p) => {
              const picked = recipients.some((x) => x.uid === p.player_userid);
              // The backend resolves linkage across all three link tables
              // (per-server, web and the pre-2026-03 legacy one); fall back
              // to the roster match for older API responses.
              const linked = linkedByUid[(p.player_userid || "").toLowerCase()];
              const isLinked = p.linked ?? Boolean(linked);
              const handle = p.discord_username || linked?.discord_username;
              return (
                <li key={p.player_userid}>
                  <button
                    type="button"
                    onClick={() => toggleRecipient({
                      player_userid: p.player_userid,
                      player_name: p.player_name || p.player_userid,
                      server_name: server,
                    })}
                    className={`w-full text-left flex items-center gap-2 rounded px-2 py-2 min-h-11 lg:min-h-0 border transition-colors ${
                      picked
                        ? "border-[var(--teal)] bg-[rgba(0,229,255,0.12)]"
                        : "border-transparent hover:bg-white/5"
                    }`}
                    data-testid={`give-online-${p.player_userid}`}
                  >
                    <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs truncate">
                        {p.player_name}
                        {handle && (
                          <span className="text-[var(--muted)]"> · @{handle}</span>
                        )}
                        {p.just_relinked && (
                          <span className="text-emerald-300"> · re-linked just now</span>
                        )}
                      </span>
                      <span className="block text-[10px] font-mono text-[var(--muted)] truncate">
                        {p.player_userid}
                        {isLinked && p.needs_relink && (
                          <span className="text-amber-300 font-sans"> · needs re-link</span>
                        )}
                        {!isLinked && " · not linked"}
                      </span>
                    </span>
                    {picked && <CheckCircle2 size={14} className="text-[var(--teal)] shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Selected recipients as removable chips */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="label !mb-0 flex items-center gap-1.5">
            <Users size={12} /> Recipients ({recipients.length})
          </span>
          {recipients.length > 0 && (
            <button
              type="button"
              onClick={() => setRecipients([])}
              className="text-[10px] uppercase tracking-wider text-[var(--muted)] hover:text-[var(--danger)]"
              data-testid="give-recipients-clear"
            >
              Clear
            </button>
          )}
        </div>
        {recipients.length === 0 ? (
          <p className="text-xs text-[var(--muted)]">Nobody selected — pick from the list below.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="give-recipient-chips">
            {recipients.map((r) => {
              const on = isOnline(r.uid);
              return (
                <span
                  key={r.uid}
                  className={`inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full text-xs border ${
                    on ? "border-emerald-400/40 bg-emerald-400/10" : "border-white/10 bg-black/30"
                  }`}
                  title={on ? "Online" : "Not detected in-game"}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${on ? "bg-emerald-400" : "bg-white/25"}`} />
                  <span className="truncate max-w-[140px]">{r.name}</span>
                  <button
                    type="button"
                    onClick={() => toggleRecipient({ player_userid: r.uid })}
                    className="grid place-items-center h-6 w-6 rounded-full hover:text-[var(--danger)]"
                    aria-label={`Remove ${r.name}`}
                  >
                    <X size={11} />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <Field label={`All linked players (${players.length})`}>
        <input
          className="input mb-2"
          placeholder="Filter by name, Discord, or UID…"
          value={playerFilter}
          onChange={(e) => setPlayerFilter(e.target.value)}
          data-testid="give-player-filter"
        />
        <ul className="max-h-56 overflow-y-auto space-y-1 pr-0.5" data-testid="give-player-list">
          {players.map((p) => {
            const uid = p.player_userid || "";
            const picked = recipients.some((x) => x.uid === uid);
            const on = isOnline(uid);
            return (
              <li key={`${p.user_id}-${uid}-${p.server_name}`}>
                <button
                  type="button"
                  onClick={() => toggleRecipient(p)}
                  className={`w-full text-left flex items-center gap-2 rounded-md px-2 py-2 min-h-11 lg:min-h-0 border transition-colors ${
                    picked
                      ? "border-[var(--teal)] bg-[rgba(0,229,255,0.1)]"
                      : "border-transparent hover:bg-white/5"
                  }`}
                  data-testid={`give-player-${uid}`}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${on ? "bg-emerald-400" : "bg-white/20"}`}
                        title={on ? "Online" : "Not detected in-game"} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs truncate">
                      {p.player_name}
                      {p.discord_username && <span className="text-[var(--muted)]"> · @{p.discord_username}</span>}
                    </span>
                    <span className="block text-[10px] font-mono text-[var(--muted)] truncate">
                      {PLATFORM_BADGE[p.platform] || ""} {uid}
                      {p.server_name
                        ? ` · ${p.server_name.replace(/Chillet's /, "")}`
                        : p.link_source === "legacy" ? " · any server" : ""}
                      {p.needs_relink && (
                        <span className="text-amber-300 font-sans" title="Old-style link: the stored id is a platform id, not a deliverable game UID. They'll be fixed automatically next time they log in.">
                          {" "}· needs re-link
                        </span>
                      )}
                    </span>
                  </span>
                  {picked && <CheckCircle2 size={14} className="text-[var(--teal)] shrink-0" />}
                </button>
              </li>
            );
          })}
          {players.length === 0 && (
            <li className="text-xs text-[var(--muted)] py-2">No linked players found.</li>
          )}
        </ul>
      </Field>

      <Field label="Search all users (Discord / in-game)">
        <UserSearchInput
          onPick={(row) => {
            if (!row.player_userid) return;
            toggleRecipient({
              player_userid: row.player_userid,
              player_name: row.player_name || row.discord_username || row.player_userid,
              server_name: row.server_name || server,
            });
          }}
          scope="player_userid"
          serverName={crossServer ? null : server}
          placeholder="Start typing a name to add them…"
          dataTestid="give-user-search"
        />
      </Field>

      <Field label="…or paste a Player UID">
        <div className="flex flex-wrap gap-2">
          <input
            className="input font-mono flex-1 min-w-[180px]"
            placeholder="steam_76561198…"
            value={manualUid}
            onChange={(e) => setManualUid(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }}
            data-testid="give-manual-uid"
          />
          <button type="button" onClick={addManual} className="btn btn-sm" data-testid="give-manual-add">
            <Plus size={12} /> Add
          </button>
        </div>
      </Field>
    </div>
  );
}

/* =========================================================================
   Queue
   ======================================================================= */

function QueuePanel({
  queue, setQueue, patchRow, removeRow, duplicateRow, onEditSpec,
  recipients, running, onExecute, offlineCount,
}) {
  const totalSends = queue.length * Math.max(1, recipients.length);
  return (
    <div className="glass-card p-4" data-testid="give-queue-panel">
      <div className="flex items-center justify-between mb-2">
        <h4 className="label !mb-0">Queue ({queue.length})</h4>
        {queue.length > 0 && (
          <button
            type="button"
            onClick={() => setQueue([])}
            className="text-[10px] uppercase tracking-wider text-[var(--muted)] hover:text-[var(--danger)] transition-colors"
            data-testid="give-queue-clear"
          >
            Clear all
          </button>
        )}
      </div>

      {queue.length === 0 && (
        <p className="text-xs text-[var(--muted)]">Pick something from the catalog →</p>
      )}

      <ul className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="give-queue">
        {queue.map((e, i) => {
          const custom = e.type === "pal" && specIsCustom(e.pal_spec);
          return (
            <li key={i} className="bg-black/30 rounded p-2 text-xs">
              <div className="flex items-start gap-2">
                {e.image_url ? (
                  <img src={e.image_url} alt="" className="w-8 h-8 rounded object-contain shrink-0 bg-black/40" loading="lazy" />
                ) : (
                  <div className="w-8 h-8 rounded bg-white/5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="badge text-[var(--muted)] border-white/10">{e.type}</span>
                    {custom && (
                      <span className="badge text-[var(--teal)] border-[var(--teal)]" title="Delivers via givepal_j with a full spec">
                        <Sparkles size={9} /> custom
                      </span>
                    )}
                    <span className="truncate">
                      {e.item_name || (e.type === "xp" ? `${e.amount?.toLocaleString()} XP` : e.item_id)}
                    </span>
                  </div>

                  {custom && (
                    <div className="text-[10px] text-[var(--muted)] mt-0.5 truncate">
                      Lv {e.pal_spec.level}
                      {e.pal_spec.talents?.some((t) => t > 0) && ` · IV ${e.pal_spec.talents.join("/")}`}
                      {e.pal_spec.rank > 0 && ` · ${e.pal_spec.rank}★`}
                      {e.pal_spec.passives?.length > 0 && ` · ${e.pal_spec.passives.join(", ")}`}
                      {e.pal_spec.is_lucky && " · ✨Lucky"}
                    </div>
                  )}

                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {e.type === "item" && (
                      <>
                      <label className="inline-flex items-center gap-1">×
                        <input
                          className="input inline-block w-24" type="number" min="1" value={e.amount}
                          onFocus={(ev) => ev.target.select()}
                          onChange={(ev) => patchRow(i, { amount: parseInt(ev.target.value, 10) || 1 })}
                          data-testid={`give-row-amount-${i}`}
                        />
                      </label>
                      {/* One-tap jumps for the amounts that actually come up,
                          so a 9,999 stack doesn't need the keyboard. */}
                      {[999, 9999].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => patchRow(i, { amount: n })}
                          className={`btn btn-sm ${e.amount === n ? "" : "btn-secondary"} !px-2`}
                          title={`Set to ${n.toLocaleString()}`}
                          data-testid={`give-row-qty-${n}-${i}`}
                        >
                          {n.toLocaleString()}
                        </button>
                      ))}
                      </>
                    )}
                    {e.type === "pal" && !custom && (
                      <label className="inline-flex items-center gap-1">Lv
                        <input
                          className="input inline-block w-16" type="number" min="1" value={e.level}
                          onChange={(ev) => patchRow(i, { level: parseInt(ev.target.value, 10) || 1 })}
                        />
                      </label>
                    )}
                    {e.type === "xp" && (
                      <input
                        className="input inline-block w-28" type="number" min="1" value={e.amount}
                        onChange={(ev) => patchRow(i, { amount: parseInt(ev.target.value, 10) || 1 })}
                      />
                    )}
                    {e.type === "pal" && (
                      <button
                        type="button"
                        onClick={() => onEditSpec(i)}
                        className="btn btn-sm btn-secondary !px-2"
                        data-testid={`give-edit-spec-${i}`}
                      >
                        <Wand2 size={11} /> {custom ? "Edit spec" : "Customize"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => duplicateRow(i, 1)} className="btn btn-sm btn-secondary !px-1.5"
                          title="Duplicate" data-testid={`give-duplicate-${i}`}>+1</button>
                  <button onClick={() => duplicateRow(i, 9)} className="btn btn-sm btn-secondary !px-1.5"
                          title="Duplicate ×9" data-testid={`give-duplicate-9-${i}`}>+9</button>
                  <button onClick={() => removeRow(i)} className="btn btn-sm btn-danger !px-2"
                          aria-label="Remove entry" data-testid={`give-remove-${i}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {queue.length > 0 && recipients.length > 1 && (
        <p className="text-[10px] text-[var(--muted)] mt-2">
          {queue.length} entries × {recipients.length} recipients = <strong>{totalSends}</strong> RCON sends
          (~{Math.ceil(totalSends * 0.25)}s).
        </p>
      )}
      {offlineCount > 0 && (
        <p className="text-[10px] text-amber-300 mt-1 flex items-center gap-1">
          <AlertTriangle size={11} /> {offlineCount} selected recipient(s) appear offline.
        </p>
      )}

      <button
        onClick={onExecute}
        className="btn w-full mt-3"
        disabled={running || queue.length === 0 || recipients.length === 0}
        data-testid="give-execute-button"
      >
        {running ? "Sending…" : `Send ${queue.length} to ${recipients.length || "?"} player${recipients.length === 1 ? "" : "s"}`}
      </button>
    </div>
  );
}

/* =========================================================================
   Results
   ======================================================================= */

function ResultsPanel({ results, onRetry, onDismiss }) {
  const failedCount = (results.results || []).filter((r) => !r.ok).length;
  return (
    <div className="glass-card p-3 text-xs" data-testid="give-last-results">
      <div className="flex items-center justify-between mb-2">
        <h4 className="label !mb-0">Last batch — {results.delivered}/{results.total} ok</h4>
        <button type="button" onClick={onDismiss} className="grid place-items-center h-7 w-7 rounded hover:text-[var(--bone)] text-[var(--muted)]" aria-label="Dismiss results">
          <X size={13} />
        </button>
      </div>

      {(results.recipients || []).length > 1 && (
        <ul className="space-y-0.5 mb-2">
          {results.recipients.map((r) => (
            <li key={`${r.server}-${r.player_userid}`} className="flex items-center justify-between gap-2">
              <span className="font-mono truncate text-[10px] text-[var(--muted)]">{r.player_userid}</span>
              <span className={r.delivered === r.total ? "text-[var(--teal)]" : "text-[var(--danger)]"}>
                {r.delivered}/{r.total}
              </span>
            </li>
          ))}
        </ul>
      )}

      <ul className="space-y-1 max-h-40 overflow-y-auto">
        {(results.results || []).map((r, i) => (
          <li key={i} className={r.ok ? "text-[var(--teal)]" : "text-[var(--danger)]"}>
            {r.ok ? "✓" : "✗"} {r.label}
            {(r.response || r.error) && (
              <span className="text-[var(--muted)]"> — {r.error || r.response}</span>
            )}
          </li>
        ))}
      </ul>

      {failedCount > 0 && (
        <button type="button" onClick={onRetry} className="btn btn-sm btn-secondary w-full mt-2" data-testid="give-retry-failed">
          <RotateCcw size={11} /> Rebuild queue from {failedCount} failure{failedCount === 1 ? "" : "s"}
        </button>
      )}
    </div>
  );
}

/* =========================================================================
   Catalog
   ======================================================================= */

function CatalogBrowser({
  kind, setKind, catalog, catalogBusy, categories, category, setCategory,
  search, setSearch, matches, shown, visible, setVisible, addQty, setAddQty,
  onAdd, onAddXp,
}) {
  const searchRef = useRef(null);
  return (
    <div className="glass-card p-4 space-y-3 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        {["item", "pal", "egg", "tech"].map((k) => (
          <button
            key={k}
            onClick={() => { setKind(k); setSearch(""); setCategory(""); searchRef.current?.focus(); }}
            className={`btn btn-sm ${kind === k ? "" : "btn-secondary"}`}
            data-testid={`give-kind-${k}`}
          >
            {/* Counts come from the loaded catalog rather than hardcoded
                literals, which used to drift every time gamedata changed. */}
            {k}s{kind === k && !catalogBusy ? ` (${catalog.length})` : ""}
          </button>
        ))}
        <button onClick={onAddXp} className="btn btn-sm btn-secondary" data-testid="give-add-xp">+ XP</button>
      </div>

      <div className="flex items-center gap-2 flex-wrap bg-black/30 rounded-md p-2 border border-white/5" data-testid="give-qty-row">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] mr-1">Qty per click</span>
        <input
          type="number" min={1}
          max={kind === "tech" ? 1 : (kind === "item" ? 99999 : MAX_BULK)}
          value={addQty}
          onChange={(e) => setAddQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
          // Select-on-focus so typing a new amount replaces the old one
          // instead of appending to it.
          onFocus={(e) => e.target.select()}
          className="input w-28"
          data-testid="give-qty-input"
          disabled={kind === "tech"}
          title={kind === "tech" ? "Tech can only be learned once per player" : ""}
        />
        <div className="flex items-center gap-1 flex-wrap">
          {QTY_PRESETS.filter((n) => kind !== "tech" && (kind === "item" || n <= MAX_BULK)).map((n) => (
            <button
              key={n} type="button" onClick={() => setAddQty(n)}
              className={`btn btn-sm ${addQty === n ? "" : "btn-secondary"} !px-2`}
              data-testid={`give-qty-preset-${n}`}
            >
              ×{n.toLocaleString()}
            </button>
          ))}
        </div>
        {kind !== "item" && kind !== "tech" && (
          <span className="text-[10px] text-[var(--muted)] ml-1">
            pals/eggs queue N separate entries (max {MAX_BULK})
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-2">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
          <input
            ref={searchRef}
            className="input !pl-9"
            placeholder={`Search ${kind}s…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="give-search"
          />
        </div>
        <select className="input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="give-category-filter">
          <option value="">All categories ({catalog.length})</option>
          {categories.map(([name, count]) => (
            <option key={name} value={name}>{name} ({count})</option>
          ))}
        </select>
      </div>

      {/* The old build sliced to 300 with no indication, so "it's not in the
          list" and "it's past the cut-off" looked identical. */}
      <div className="flex items-center justify-between text-[10px] text-[var(--muted)]">
        <span>
          {catalogBusy ? "Loading catalog…" : `Showing ${shown.length} of ${matches.length}${matches.length !== catalog.length ? ` (filtered from ${catalog.length})` : ""}`}
        </span>
        {(search || category) && (
          <button type="button" onClick={() => { setSearch(""); setCategory(""); }} className="hover:text-[var(--bone)] uppercase tracking-wider">
            Reset filters
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 max-h-[55dvh] overflow-y-auto">
        {shown.map((c) => (
          <button
            key={`${kind}-${c.id}`}
            onClick={() => onAdd[kind]?.(c)}
            className="glass-card p-2 text-left text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
            data-testid={`give-catalog-${c.id}`}
            title={c.id}
          >
            {c.image ? (
              <img src={c.image} alt="" className="w-10 h-10 rounded object-contain shrink-0 bg-black/40" loading="lazy" />
            ) : (
              <div className="w-10 h-10 rounded bg-white/5 shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{c.name}</div>
              <div className="text-[10px] text-[var(--muted)] font-mono truncate">{c.id}</div>
            </div>
          </button>
        ))}
        {!catalogBusy && matches.length === 0 && (
          <div className="col-span-full text-center text-[var(--muted)] text-sm py-8">No matches.</div>
        )}
      </div>

      {shown.length < matches.length && (
        <button
          type="button"
          onClick={() => setVisible((v) => v + PAGE_SIZE)}
          className="btn btn-sm btn-secondary w-full"
          data-testid="give-show-more"
        >
          Show {Math.min(PAGE_SIZE, matches.length - shown.length)} more
        </button>
      )}
    </div>
  );
}

/* =========================================================================
   Pal spec editor
   ======================================================================= */

function PalSpecModal({ row, caps, passives, modded, onClose, onSave }) {
  const [spec, setSpec] = useState(() => row.pal_spec
    ? { ...emptySpec(row.level || 1), ...row.pal_spec }
    : emptySpec(row.level || 1));
  const [pq, setPq] = useState("");

  const set = (patch) => setSpec((s) => ({ ...s, ...patch }));
  const setTalent = (i, v) => setSpec((s) => {
    const t = [...(s.talents || [0, 0, 0])];
    t[i] = Math.max(0, Math.min(caps.iv_max, parseInt(v, 10) || 0));
    return { ...s, talents: t };
  });

  const togglePassive = (name) => setSpec((s) => {
    const cur = s.passives || [];
    if (cur.includes(name)) return { ...s, passives: cur.filter((p) => p !== name) };
    if (cur.length >= caps.passive_max) return s;
    return { ...s, passives: [...cur, name] };
  });

  const passiveMatches = useMemo(() => {
    const q = pq.trim().toLowerCase();
    const list = q ? passives.filter((p) => p.name.toLowerCase().includes(q)) : passives;
    return list.slice(0, 60);
  }, [passives, pq]);

  const maxAll = () => set({
    talents: [caps.iv_max, caps.iv_max, caps.iv_max],
    rank: caps.rank_max,
    rank_hp: caps.soul_max,
    rank_attack: caps.soul_max,
    rank_defence: caps.soul_max,
    rank_craft_speed: caps.soul_max,
    trust: caps.trust_max,
    level: caps.level_max,
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
      data-testid="give-pal-spec-modal"
    >
      <div
        className="glass-card w-full sm:max-w-3xl max-h-[88dvh] overflow-y-auto p-5 space-y-4 rounded-b-none sm:rounded-[var(--radius)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg truncate" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
              {row.item_name || row.item_id}
            </h3>
            <p className="text-[11px] text-[var(--muted)]">
              {modded
                ? `Modded server — IV up to ${caps.iv_max}, souls up to ${caps.soul_max}.`
                : `Vanilla caps — IV up to ${caps.iv_max}, souls up to ${caps.soul_max}.`}
            </p>
          </div>
          <button onClick={onClose} className="grid place-items-center h-11 w-11 -mr-2 -mt-2 text-[var(--muted)] hover:text-[var(--bone)]" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={maxAll} className="btn btn-sm">
            <Sparkles size={12} /> Max everything
          </button>
          <button type="button" onClick={() => setSpec(emptySpec(spec.level))} className="btn btn-sm btn-secondary">
            <RotateCcw size={12} /> Reset stats
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label={`Level (1–${caps.level_max})`}>
            <input className="input" type="number" min={1} max={caps.level_max} value={spec.level}
                   onChange={(e) => set({ level: Math.max(1, Math.min(caps.level_max, parseInt(e.target.value, 10) || 1)) })}
                   data-testid="spec-level" />
          </Field>
          <Field label="Gender">
            <select className="input" value={spec.gender} onChange={(e) => set({ gender: e.target.value })} data-testid="spec-gender">
              <option>Male</option><option>Female</option>
            </select>
          </Field>
          <Field label="Nickname (optional)">
            <input className="input" value={spec.nick_name || ""} maxLength={20}
                   onChange={(e) => set({ nick_name: e.target.value })} data-testid="spec-nickname" />
          </Field>
        </div>

        <div>
          <span className="label">IVs (0–{caps.iv_max})</span>
          <div className="grid grid-cols-3 gap-2">
            {["HP", "Attack", "Defence"].map((l, i) => (
              <label key={l} className="block">
                <span className="block text-[10px] text-[var(--muted)] mb-1">{l}</span>
                <input className="input" type="number" min={0} max={caps.iv_max}
                       value={spec.talents?.[i] ?? 0}
                       onChange={(e) => setTalent(i, e.target.value)}
                       data-testid={`spec-iv-${i}`} />
              </label>
            ))}
          </div>
        </div>

        <div>
          <span className="label">Condenser souls (0–{caps.soul_max})</span>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[["rank_hp", "HP"], ["rank_attack", "Attack"], ["rank_defence", "Defence"], ["rank_craft_speed", "Work"]].map(([k, l]) => (
              <label key={k} className="block">
                <span className="block text-[10px] text-[var(--muted)] mb-1">{l}</span>
                <input className="input" type="number" min={0} max={caps.soul_max} value={spec[k] ?? 0}
                       onChange={(e) => set({ [k]: Math.max(0, Math.min(caps.soul_max, parseInt(e.target.value, 10) || 0)) })}
                       data-testid={`spec-soul-${k}`} />
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
          <Field label={`Stars (0–${caps.rank_max})`}>
            <input className="input" type="number" min={0} max={caps.rank_max} value={spec.rank ?? 0}
                   onChange={(e) => set({ rank: Math.max(0, Math.min(caps.rank_max, parseInt(e.target.value, 10) || 0)) })}
                   data-testid="spec-rank" />
          </Field>
          <Field label={`Trust (0–${caps.trust_max})`}>
            <input className="input" type="number" min={0} max={caps.trust_max} value={spec.trust ?? 0}
                   onChange={(e) => set({ trust: Math.max(0, Math.min(caps.trust_max, parseInt(e.target.value, 10) || 0)) })}
                   data-testid="spec-trust" />
          </Field>
          <label className="flex items-center gap-2 min-h-11 cursor-pointer">
            <input type="checkbox" checked={!!spec.is_lucky} onChange={(e) => set({ is_lucky: e.target.checked })} data-testid="spec-lucky" />
            <span className="text-sm">Lucky ✨</span>
          </label>
          <label className="flex items-center gap-2 min-h-11 cursor-pointer">
            <input type="checkbox" checked={!!spec.is_boss} onChange={(e) => set({ is_boss: e.target.checked })} data-testid="spec-boss" />
            <span className="text-sm">Alpha/Boss</span>
          </label>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="label !mb-0">
              Passives ({spec.passives?.length || 0}/{caps.passive_max})
            </span>
            {spec.passives?.length > 0 && (
              <button type="button" onClick={() => set({ passives: [] })}
                      className="text-[10px] uppercase tracking-wider text-[var(--muted)] hover:text-[var(--danger)]">
                Clear
              </button>
            )}
          </div>

          {spec.passives?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {spec.passives.map((p) => (
                <span key={p} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full text-xs border border-[var(--teal)] bg-[rgba(0,229,255,0.1)]">
                  {p}
                  <button type="button" onClick={() => togglePassive(p)} className="grid place-items-center h-6 w-6 rounded-full hover:text-[var(--danger)]" aria-label={`Remove ${p}`}>
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <input className="input mb-2" placeholder="Search passives…" value={pq}
                 onChange={(e) => setPq(e.target.value)} data-testid="spec-passive-search" />
          <div className="max-h-44 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1">
            {passiveMatches.map((p) => {
              const picked = spec.passives?.includes(p.name);
              const full = !picked && (spec.passives?.length || 0) >= caps.passive_max;
              return (
                <button
                  key={p.name}
                  type="button"
                  disabled={full}
                  onClick={() => togglePassive(p.name)}
                  className={`text-left text-xs px-2 py-2 min-h-11 sm:min-h-0 rounded border transition-colors ${
                    picked ? "border-[var(--teal)] bg-[rgba(0,229,255,0.1)]"
                           : full ? "border-transparent opacity-30 cursor-not-allowed"
                                  : "border-transparent hover:bg-white/5"
                  }`}
                  data-testid={`spec-passive-${p.name}`}
                >
                  <span className="truncate block">
                    {p.tier > 0 && <span className="text-[var(--muted)]">T{p.tier} </span>}
                    {p.name}
                  </span>
                </button>
              );
            })}
            {passiveMatches.length === 0 && (
              <div className="col-span-full text-xs text-[var(--muted)] py-3 text-center">No passives match.</div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <button type="button" onClick={() => onSave(spec, spec.level)} className="btn flex-1 min-w-[140px]" data-testid="spec-save">
            <CheckCircle2 size={13} /> Apply spec
          </button>
          <button type="button" onClick={() => onSave(null, spec.level)} className="btn btn-secondary" data-testid="spec-clear">
            <Copy size={13} /> Plain pal instead
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Giveaways — website ↔ bot bridge
// ---------------------------------------------------------------------------
